import type { QueryKey } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import type { FilterOption } from '@/shared/hooks/useFilterOptions';
import { itemsToFilterOptions } from '@/shared/hooks/useFilterOptions';
import { selectorColumn } from '../display/selectorDisplay';
import type { ColumnDef, PickFilterDef } from '../types';

export interface ApiPickFilterInput<TId extends number | string = number> {
  id: string;
  placeholder: string;
  queryKey: QueryKey | (() => QueryKey);
  queryFn: () => Promise<unknown>;
  invalidMessage: string;
  transform?: (response: unknown) => FilterOption<TId>[];
  prefetch?: boolean;
  defaultValue?: TId | null;
  staleTime?: number;
  gcTime?: number;
  refetchOnMount?: boolean | 'always';
}

export interface TextColumnPreset<TItem> {
  id: string;
  header: string;
  value: (item: TItem) => ReactNode;
  sortable?: boolean;
  emptyDash?: string;
}

export interface NameAndCodeColumnsPreset<TItem> {
  name: TextColumnPreset<TItem>;
  code: TextColumnPreset<TItem>;
}

export function resolvePickFilter<TId extends number | string = number>(
  input: ApiPickFilterInput<TId>
): PickFilterDef<TId> {
  return {
    id: input.id,
    placeholder: input.placeholder,
    queryKey: typeof input.queryKey === 'function' ? input.queryKey() : input.queryKey,
    queryFn: input.queryFn,
    transform:
      input.transform ??
      ((response) => itemsToFilterOptions(response, input.invalidMessage) as FilterOption<TId>[]),
    prefetch: input.prefetch ?? true,
    defaultValue: input.defaultValue ?? null,
    staleTime: input.staleTime,
    gcTime: input.gcTime,
    refetchOnMount: input.refetchOnMount,
  };
}

function textColumn<TItem>(preset: TextColumnPreset<TItem>): ColumnDef<TItem> {
  return selectorColumn.text(preset.id, preset.header, preset.value, {
    sortable: preset.sortable,
    emptyDash: preset.emptyDash,
  });
}

function apiPickFilter<TId extends number | string = number>(
  input: ApiPickFilterInput<TId>
): ApiPickFilterInput<TId> {
  return input;
}

function nameAndCodeColumns<TItem>(preset: NameAndCodeColumnsPreset<TItem>): ColumnDef<TItem>[] {
  return [textColumn(preset.name), textColumn(preset.code)];
}

export const selectorPresets = {
  textColumn,
  nameAndCode: nameAndCodeColumns,
  apiPickFilter,
};
